import { DeviceTypes, InstallationType } from '@store';
import { HeatingType } from '../apis/equipment/equipment.model';

export enum AggregatedStatus {
  Error = 'Error',
  Offline = 'Offline',
  Maintenance = 'Maintenance',
  WorksProperly = 'WorksProperly',
  RemoteDiagnosticSession = 'RemoteDiagnosticSession',
  NBIoT = 'NbIotConnected',
  Locked = 'Locked',
}

export enum GatewayType {
  VitoconnectOptolink = 'VitoconnectOptolink',
  Unknown = 'Unknown',
  NestThermostat = 'NestThermostat',
  VitocomLan4 = 'VitocomLan4',
  SA171 = 'SA171',
  VitoconnectOPTO2 = 'VitoconnectOPTO2',
  SmartThermostat = 'SmartThermostat',
  Lancard = 'Lancard',
  VitoconnectOpenTherm = 'VitoconnectOpenTherm',
  SA171s = 'SA171s',
  SA180 = 'SA180',
  SA180NBIoT = 'SA180NBIoT',
  SA1800019WiFi = 'SA1800019WiFi',
  SA180Lan = 'SA180Lan',
  WiFi_SA0019 = 'WiFi_SA0019',
  WiFi_SA0041 = 'WiFi_SA0041',
  TCU201 = 'TCU201',
  TCU101 = 'TCU101',
  TCU102 = 'TCU102',
  TCU301 = 'TCU301',
}

export enum GatewayRemoteDiagnosticsStatus {
  Inactive = 'inactive',
  Initiated = 'initiated',
  Ready = 'ready',
  Active = 'active',
}

export enum TargetRealmType {
  Genesis = 'Genesis',
  DC = 'DC',
}

export enum SerialEditorType {
  User = 'User',
  DeviceCommunication = 'DeviceCommunication',
}

export enum DeviceStatusType {
  Offline = 'Offline',
  Online = 'Online',
}

export enum CommissioningTags {
  hb2MissingCertificate = 'hb2MissingCertificate',
}

export enum CommissioningBrokenGatewayUrl {
  flatRate = 'https://partnerportal.viessmann-climatesolutions.com/at/de/informieren/vitoconnect--problem-bei-der-konnektivierung.html',
  defaultUrl = 'https://partnerportal.viessmann-climatesolutions.com/de/de/informieren/vitoconnect--problem-bei-der-konnektivierung.html',
}

export enum DhwScheduleModeType {
  Individual = 'individual',
  Automatic = 'automatic',
}

export enum HeatingDeviceStatusType {
  ZeroBoilerAirDampers = 'zero boiler air dampers',
  FlushBoilerCombChamber = 'flush boiler comb chamber',
  FillBoilerDosingHopper = 'fill boiler dosing hopper',
  FillBoilerScrewConveyor = 'fill boiler screw conveyor',
  FillCombustionChamber = 'fill combustion chamber',
  IgniteBoiler = 'ignite boiler',
  IgniteBoilerFirebed = 'ignite boiler firebed',
  BoilerIgniteRecharge = 'boiler ignite, recharge',
  BoilerLoadOperation = 'boiler load operation',
  BoilerRunOn = 'boiler run-on',
  BoilerStandby = 'boiler standby',
  RunScrewConveyourTillEmpty = 'run screw conveyour till empty',
  FeedTubeWarm = 'feed tube warm',
  GrateCleaning = 'Grate cleaning',
  DayHopperEmpty = 'day hopper empty',
  CalibrateO2Probe = 'calibrate O2 probe',
  AshBoxFull = 'ash box full',
  BoilerTestMode = 'boiler test mode',
  BoilerResidHeatUtilisation = 'boiler resid heat utilisation',
  TransferHeat = 'transfer heat',
  Burner = 'burner',
  CloseBoilerValves = 'close boiler valves',
  BufferDrawing = 'buffer drawing',
  EnableAdditionalBoiler = 'enable additional boiler',
  BoilerHeatUp = 'boiler heat-up',
  BoilerOff = 'boiler off',
  FillVacuumModule = 'fill vacuum module',
  SolarBufferReheatingSuppression = 'solar-buffer reheating suppression',
  BoilerRechargingAllowed = 'boiler recharging allowed',
  ChamberDoorOpen = 'chamber door open',
}

export enum HeatingSolarStatusType {
  Standby = 'standby',
  Automatic = 'automatic',
  CollectorTooCold = 'collectorTooCold',
  RiskOfOverheating = 'riskOfOverheating',
  DhwHeating = 'dhwHeating',
  BufferCharging = 'bufferCharging',
  MaximalDhwTemperature = 'maximalDhwTemperature',
  Wait = 'wait',
  Flush = 'flush',
  RiskOfBufferOverheating = 'riskOfBufferOverheating',
}

export interface InstallationGeolocation {
  latitude: number;
  longitude: number;
  timeZone: string;
}

// https://wiki.viessmann-climatesolutions.com/pages/viewpage.action?spaceKey=DCC&title=Roles
export enum DeviceRoles {
  Actuator = 'type:actuator',
  B2C = 'type:B2C',
  Boiler = 'type:boiler',
  BoilerRenewable = 'type:boiler;renewable',
  BackupCapability = 'capability:backup',
  CascadeLead = 'type:cascadeV1Lead',
  CascadeV2Lag = 'type:cascadeV2Lag',
  CascadeV2Lead = 'type:cascadeV2Lead',
  ClimateSensor = 'type:climateSensor',
  Co2Sensor = 'type:co2Sensor',
  ConsolidatorElectric = 'type:consolidator;electric',
  E3 = 'type:E3',
  Ecotronic = 'type:product;Ecotronic',
  EcoWater = 'type:water;softener',
  Ess = 'type:ess',
  FuelCell = 'type:fuelcell',
  FhtChannel = 'type:fhtChannel',
  FhtMain = 'type:fhtMain',
  Gateway = 'type:gateway',
  GenericGateway = 'type:gateway;',
  GatewayOT2 = 'type:gateway;VitoconnectOpto2/OT2',
  GatewayTCU300 = 'type:gateway;TCU300',
  GatewayTCU200 = 'type:gateway;TCU200',
  GatewayTCU100 = 'type:gateway;TCU100',
  GatewayTCU = 'type:gateway;TCU',
  GatewayConfiguration = 'type:gatewayConfiguration',
  GatewayVitoconnectOpto1 = 'type:gateway;VitoconnectOpto1',
  GatewayVitoconnectOpto3 = 'type:gateway;VitoconnectOpto3',
  Heatpump = 'type:heatpump',
  DHWHeatpump = 'type:dhwHeatpump',
  Legacy = 'type:legacy',
  GenericProduct = 'type:product;',
  ProductHeatpump = 'type:product;Heatpump',
  ProductVitocharge = 'type:product;Vitocharge',
  ProductVitocrossalNA = 'type:product;Vitocrossal_NA',
  PhotovoltaicCAN = 'type:photovoltaic;CAN',
  PhotovoltaicInternal = 'type:photovoltaic;Internal',
  PhotovoltaicSolarLogInverter = 'type:photovoltaic;SolarLog',
  Radiator = 'type:radiator',
  RemoteControllerVitotrol100E = 'type:remoteController;Vitotrol100E',
  RemoteControllerVitotrol300E = 'type:remoteController;Vitotrol300E',
  Repeater = 'type:repeater',
  Sensor = 'type:sensor',
  SmartRoomDevice = 'type:smartRoomDevice',
  SmartRoomDeviceActuator = 'type:smartRoomDevice;actuator',
  SmartRoomDeviceSensor = 'type:smartRoomDevice;sensor',
  SolarLogGateway = 'type:accessory;solarLogGateway',
  VCS = 'type:accessory;vehicleChargingStation',
  Ventilation = 'type:ventilation',
  VentilationPurifier = 'type:ventilation;purifier',
  Virtual = 'type:virtual',
  VirtualHems = 'type:virtual;hems',
  VirtualSRC = 'type:virtual;smartRoomControl',
  VirtualHeatDemandControl = 'type:virtual;heatDemandControl',
  Vitodens100 = 'type:product;Vitodens_100',
  Vitodens050 = 'type:product;Vitodens_050',
  WaterSoftener = 'type:water;softener',
  WaterLeakageDetection = 'type:water;leakageDetection',
  ZigbeeCoordinator = 'capability:zigbeeCoordinator',
  Vitodens100NA = 'type:product;Vitodens_100_NA',
  Vitodens200NA = 'type:product;Vitodens_200_NA',
  SrcHoliday = 'capability:src;holiday',
  ProductVitopure = 'type:product;Vitopure',
  SolarLogPVHeater = 'type:accessory;solarLogPVHeater',
  Others = 'Others',
  PrefixViessmann = 'type:brand;Viessmann',
  PrefixCarrier = 'type:brand;Carrier',
  PrefixToshiba = 'type:brand;Toshiba',
  PrefixRiello = 'type:brand;Riello',
  PrefixBeretta = 'type:brand;Beretta',
  rcSensor = 'type:rcSensor',
  Vitovalor300 = 'type:product;Vitovalor_300',
  ViAir300 = 'type:product;ViAir_300',
  VentillationCentral = 'type:ventilation;central',
  SystemControllerSCU = 'type:systemController;SCU',
  SystemController = 'type:product;SystemController',
  GenericConsumer = 'type:product;GenericConsumer',
  OBNG = 'type:OBNG',
  // product family
  ProductFamilyVitotrol = 'type:productFamily;Vitotrol',
  ProductFamilyIndoorUnit = 'type:productFamily;IndoorUnit',
  ProductFamilyOutdoorUnit = 'type:productFamily;OutdoorUnit',
  // business domain
  BusinessDomainHeating = 'type:businessDomain;heating',
  //interface roles
  InterfaceOutsideTemperature = 'interface:system;outsideTemperature',
  InterfaceSystemStandby = 'interface:system;standby',
  InterfaceDryContact = 'interface:dryContact',
  InterfaceCompressor = 'interface:compressor',
  InterfaceNoiseReduction = 'interface:noiseReduction',
}

export enum DeviceCapability {
  hmuPT2 = '0002_HMU_PT2',
}

export enum DeviceIds {
  Main = '0',
  Eebus = 'EEBUS',
  Gateway = 'gateway',
  Hems = 'HEMS',
  RoomControl = 'RoomControl-1',
  HemsAlternative = 'hems-0',
  Tcu = 'tcu',
  TcuAlternative = 'TCU',
  Eebus1 = 'EEBUS-1',
  Space = '0000000000000000',
}

export interface DeviceResponse {
  bmuSerial: string;
  bmuSerialEditor: SerialEditorType;
  boilerSerial: string | null;
  boilerSerialEditor: SerialEditorType;
  createdAt: string;
  deviceType: DeviceTypes;
  editedAt: string;
  gatewaySerial: string;
  id: string;
  modelId: string;
  roles: DeviceRoles[];
  status: DeviceStatusType;
  isBoilerSerialEditable: boolean;
}

export interface Device extends DeviceResponse {
  isSelected?: boolean;
  isStandalone?: boolean;
  isMainDeviceOfGateway?: boolean;
  isLeadDeviceInCascades?: boolean;
  marketingName?: string;
  gatewayType?: string;
  deviceImage?: string;
  shortDeviceName?: string;
  canEditBoilerSerial?: boolean;
  maintenancePackageLink?: string;
  equipmentNumber?: string;
  error?: any;
}

export interface Gateway {
  aggregatedStatus: AggregatedStatus;
  autoUpdate: boolean;
  createdAt: string;
  description: string;
  devices?: Device[];
  firmwareUpdateFailureCounter: number;
  gatewayType: GatewayType;
  installationId: number | null;
  lastStatusChangedAt: string;
  producedAt: string;
  registeredAt: string;
  serial: string;
  targetRealm: TargetRealmType;
  version: string;
  otaOngoing?: boolean;
  roles: string[];
}
export interface Address {
  city: string;
  country: string;
  faxNumber: string;
  geolocation: InstallationGeolocation;
  houseNumber: string;
  phoneNumber: string;
  region: string;
  street: string;
  zip: string;
}

export interface BuildingInfo {
  address?: Address;
  buildingName?: string;
  buildingEmail?: string;
  buildingPhone?: string;
}
export interface Installation extends BuildingInfo {
  address: Address;
  aggregatedStatus: AggregatedStatus;
  description: string;
  endUserWlanCommissioned: boolean;
  gateways: Gateway[];
  heatingType: HeatingType;
  id: number;
  installationType: InstallationType;
  ownedByMaintainer: boolean;
  registeredAt: string;
  servicedBy: string;
  updatedAt: string;
  withoutViCareUser: boolean;
  ownershipType: OwnershipType;
}

export enum OwnershipType {
  None = 'None',
  ResidentialEndUser = 'ResidentialEndUser',
  PreCommissioning = 'PreCommissioning',
  Oma = 'Oma',
  CommercialV1 = 'CommercialV1',
  CommercialV2 = 'CommercialV2',
}
